/* eslint-disable sonarjs/cognitive-complexity */
import {
  BaSeButton,
  BaSeInput,
  BaSeLoading,
  BaSeParagraph,
  BaSeTheme,
} from '@base/react';
import * as Sebrae from '@sebrae/react-core';
import * as React from 'react';
import { useUserController } from '../../../hooks/controllers/user-controller';
import { useModule } from '../../../hooks/module/module';
import { EMAIL_REGEX } from '../../../utils/validators/constants';

import {
  Background,
  ButtonAccess,
  ContainerHome,
  InfoText,
  LinkAqui,
  TextAccess,
  TextGroup,
  WrapperLink,
} from './manager-home-styled';

const cache = Sebrae.StorageUtil.inTemporaryStore();
const cacheKeySebrae = '@empretec:sebrae';

const authText = (
  loading,
  navigateTo,
  resendEmailValidateUser,
  sendEmailValidateUser,
  input,
  setInput,
  showInput,
  setShowInput,
  messageValid,
  setMessageValid,
  showFormEmail,
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const isValidEmail = (email) => {
    if (!email || email?.trim() === '') {
      setMessageValid('Campo obrigatório!');
      return false;
    }

    if (!EMAIL_REGEX.test(email)) {
      setMessageValid('E-mail inválido!');
      return false;
    }

    setMessageValid('');
    return true;
  };
  return (
    <InfoText top={500} maxWidth={320}>
      {loading ? (
        <BaSeLoading />
      ) : (
        showFormEmail && (
          <TextGroup>
            {showInput ? (
              <React.Fragment>
                <BaSeParagraph color={BaSeTheme.colors.defaultColors.white}>
                  Caro usuário,
                </BaSeParagraph>
                <BaSeParagraph color={BaSeTheme.colors.defaultColors.white}>
                  Caso você seja um gestor do Sebrae, por favor informe o seu
                  e-mail interno para validação do seu usuário e liberação de
                  acesso.
                </BaSeParagraph>

                <BaSeInput
                  placeholder="Informe um e-mail Sebrae"
                  inputMode="email"
                  width={'300px'}
                  value={input}
                  onChange={setInput}
                  complement={messageValid}
                  isDisabled={!showInput}
                />

                <BaSeButton
                  onClick={() => {
                    if (isValidEmail(input)) {
                      sendEmailValidateUser(input).then(() => {
                        setShowInput(false);
                      });
                    }
                  }}
                  isLoading={loading}
                  value="ENVIAR"
                  width="100px"
                  isDisabled={!showInput}
                />

                <BaSeParagraph color={BaSeTheme.colors.defaultColors.white}>
                  Caso seja um candidato do Empretec, sua entrevista pode ser
                  acessada pelo link:
                </BaSeParagraph>
                <LinkAqui
                  color={BaSeTheme.colors.gestao.azulAqua44}
                  onClick={() => navigateTo('/candidato')}
                >
                  https://empretec.sebrae.com.br/candidato
                </LinkAqui>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <div>
                  <BaSeParagraph color={BaSeTheme.colors.defaultColors.white}>
                    Atenção Gestor, foi enviado um e-mail para você com um link
                    de acesso.
                  </BaSeParagraph>
                  <BaSeParagraph color={BaSeTheme.colors.defaultColors.white}>
                    Por favor, acesse sua caixa de mensagem para confirmar sua
                    autenticação.
                  </BaSeParagraph>
                </div>
                <WrapperLink>
                  <BaSeParagraph color={BaSeTheme.colors.defaultColors.white}>
                    Caso não tenha recebido o e-mail,
                  </BaSeParagraph>
                  <LinkAqui
                    color={BaSeTheme.colors.gestao.azulAqua44}
                    onClick={() => {
                      resendEmailValidateUser();
                    }}
                  >
                    clique aqui.
                  </LinkAqui>
                </WrapperLink>
              </React.Fragment>
            )}
          </TextGroup>
        )
      )}
    </InfoText>
  );
};

const unAuthText = (login) => {
  return (
    <React.Fragment>
      <ButtonAccess onClick={() => login()} />
      <InfoText top={640} maxWidth={493}>
        <TextAccess>
          <BaSeParagraph color={BaSeTheme.colors.institucionais.cinzaSebrae45}>
            Por aqui você tem acesso ao sistema de inscrições online. Com esse
            sistema você poderá cadastrar novos candidatos ao Empretec, enviar
            e-mail com o link para inscrição e também visualizar os resultados
            das entrevistas realizadas.
          </BaSeParagraph>
        </TextAccess>
        <BaSeParagraph color={BaSeTheme.colors.institucionais.cinzaSebrae45}>
          Seu papel é muito importante nesse processo.
        </BaSeParagraph>
      </InfoText>
    </React.Fragment>
  );
};

const showContent = (
  isAuthenticated,
  loading,
  navigateTo,
  login,
  resendEmailValidateUser,
  sendEmailValidateUser,
  input,
  setInput,
  showInput,
  setShowInput,
  messageValid,
  setMessageValid,
  showFormEmail,
) => {
  return isAuthenticated
    ? authText(
        loading,
        navigateTo,
        resendEmailValidateUser,
        sendEmailValidateUser,
        input,
        setInput,
        showInput,
        setShowInput,
        messageValid,
        setMessageValid,
        showFormEmail,
      )
    : unAuthText(login);
};

export const ManagerHome = () => {
  const { isAuthenticated, login } = Sebrae.useAuth();
  const { navigateTo, getRouteParam } = Sebrae.useSebraeRouter();
  const { removeCache } = useModule();
  const {
    loading,
    checkUser,
    grantAccess,
    resendEmailValidateUser,
    sendEmailValidateUser,
  } = useUserController();

  const [input, setInput] = React.useState();
  const [showInput, setShowInput] = React.useState();
  const [showFormEmail, setShowFormEmail] = React.useState();
  const [token] = React.useState(getRouteParam('token'));
  const [messageValid, setMessageValid] = React.useState();

  const check = React.useCallback(() => {
    checkUser().then((data) => {
      if (!data?.showFormEmail) {
        return navigateTo('/gestor/entrevista');
      }

      setShowInput(data?.showInputEmail);
      setShowFormEmail(data?.showFormEmail);
    });
  }, [checkUser, navigateTo]);

  const handleGrantAccess = React.useCallback(
    (cpf) => {
      grantAccess(cpf).then(() => {
        return navigateTo('/gestor/entrevista', { forceReload: true });
      });
    },
    [grantAccess, navigateTo],
  );

  React.useEffect(() => {
    if (isAuthenticated) {
      if (token) {
        return handleGrantAccess(token);
      }

      check();
    }

    removeCache();
    cache.removeItem(cacheKeySebrae);
  }, [check, handleGrantAccess, isAuthenticated, removeCache, token]);

  return (
    <ContainerHome>
      <Background>
        {showContent(
          isAuthenticated,
          loading,
          navigateTo,
          login,
          resendEmailValidateUser,
          sendEmailValidateUser,
          input,
          setInput,
          showInput,
          setShowInput,
          messageValid,
          setMessageValid,
          showFormEmail,
        )}
      </Background>
    </ContainerHome>
  );
};
